import {
	Alert,
	Box,
	Button,
	Flashbar,
	FlashbarProps,
	Modal,
	SpaceBetween,
	Wizard,
} from '@amzn/awsui-components-console';
import { useHistory } from 'react-router';
import React, { useState, useEffect, useRef } from 'react';
import { useFetchForSDS } from 'src/hooks/useFetchForSDS';
import useFetchOnUserActionUsingSDS from 'src/hooks/useFetchOnUserActionUsingSDS';
import {
	CreateWorkflowDefinitionResponse,
	WorkflowIntention,
} from '@amzn/snow-nxt-workflow-service-typescript-client';
import { GetWorkflowDefinitionResponse } from '@amzn/snow-nxt-workflow-service-typescript-client';
import { SNOW_NXT_WORKFLOW_ENDPOINT } from 'src/config';
import { invalidateWorkflowCache } from 'src/components/commonComponents/WorkflowTable/useWorkflowData';
import { invalidatePermissionCache } from 'src/components/commonComponents/WorkflowTable/workflowPermissionUtils';
import { useStore } from 'src/stores/store';
import { Store } from 'src/stores/store.interface';
import { useAuthContext } from 'src/providers/AuthContext';
import { useWorkflowEditPermission } from 'src/hooks/useWorkflowEditPermission';
import { useWorkflowCreatePermission } from 'src/hooks/useWorkflowCreatePermission';
import './CreateSelfServeWorkflow.scss';
import { getSelectedOptions } from 'src/components/createWorkflow/CreateWorkflowUtils';
import { DOMAIN_OPTIONS } from 'src/components/createWorkflow/CreateWorkflowConstants';
import { CreateSelfServeWorkflowProps } from './CreateSelfServeWorkflow.interface';
import { v4 as uuidv4 } from 'uuid';
import { ApiStatus } from 'src/constants/commonEnums';
import {
	WorkflowDefinitionStatus,
	WorkflowDefinition,
} from '@amzn/snow-nxt-workflow-service-typescript-client';
import { LoadingOverlay } from 'src/components/commonComponents/loadingOverlay/loadingOverlay';
import { getWorkflowSteps, STEP_METADATA, STEP_DEFINITION, STEP_AUTO_POPULATION, STEP_REVIEW } from './CreateSelfServeWorkflowUtils';
import { getConfigureAttributeSaver } from 'src/components/createWorklow/configureAttributeStep/configureAttributeSaver';

export default function CreateSelfServeWorkflow({
	workflowId,
}: CreateSelfServeWorkflowProps): JSX.Element {
	const urlSearchParams = new URLSearchParams(window.location.hash);

	if (!workflowId) {
		workflowId =
			urlSearchParams.get('#/createWorkflow?workflowId') || undefined;
	}

	const activeStepIndexForCreateWF = useStore(
		(state: Store) => state.activeStepIndexForCreateWF
	);
	const isModalVisibleForCreateWF = useStore(
		(state: Store) => state.isModalVisibleForCreateWF
	);
	const isExitModalVisibleForCreateWF = useStore(
		(state: Store) => state.isExitModalVisibleForCreateWF
	);
	const isSubmitLoadingForCreateWF = useStore(
		(state: Store) => state.isSubmitLoadingForCreateWF
	);
	const setActiveStepIndexForCreateWF = useStore(
		(state: Store) => state.setActiveStepIndexForCreateWF
	);
	const setIsModalVisibleForCreateWF = useStore(
		(state: Store) => state.setIsModalVisibleForCreateWF
	);
	const setIsExitModalVisibleForCreateWF = useStore(
		(state: Store) => state.setIsExitModalVisibleForCreateWF
	);
	const setIsSubmitLoadingForCreateWF = useStore(
		(state: Store) => state.setIsSubmitLoadingForCreateWF
	);
	const setIstheSubmitButtonClickedForCreateWF = useStore(
		(state: Store) => state.setIstheSubmitButtonClickedForCreateWF
	);
	const setIsCreateWorkflowError = useStore(
		(state: Store) => state.setIsCreateWorkflowError
	);
	const isCreateWorkflowError = useStore(
		(state: Store) => state.isCreateWorkflowError
	);
	const isWFDefinitionUpdating = useStore(
		(state: Store) => state.isWFDefinitionUpdating
	);
	const { taskDefinitions, dependencies } = useStore(
		(state: Store) => state.WFDefinition
	);
	const fetchWFDefinition = useStore(
		(state: Store) => state.fetchWFDefinition
	);

	const workflowName = useStore((state: Store) => state.workflowName);
	const entityType = useStore((state: Store) => state.entityType);
	const description = useStore((state: Store) => state.workflowDescription);
	const approvalRequiredToStartExecution = useStore((state: Store) => state.approvalRequiredToStartExecution);
	const batchExecutionRequired = useStore((state: Store) => state.batchExecutionRequired);
	const isReRunAllowed = useStore((state: Store) => state.isReRunAllowed);
	const usableAsSubWorkflow = useStore((state: Store) => state.usableAsSubWorkflow);
	const workflowIntention = useStore((state: Store) => {
		return state.workflowIntention;
	});

	const selectedOptions = useStore(
		(state: Store) => state.selectedDomainOptions
	);

	const submitWorkflowDefinitionCanvasStep = useStore(
		(state: Store) => state.submitWorkflowDefinitionCanvasStep
	);
	const [redirectUrl, setRedirectUrl] = React.useState('');

	const workflowCreationMetaData = useStore(
		(state: Store) => state.workflowCreationMetaData
	);

	const { workflowId: workflowIdFromGetAPI, version } =
		workflowCreationMetaData;

	const updateWorkflowName = useStore((state: Store) => state.setWorkflowName);
	const setEntityType = useStore((state: Store) => state.setEntityType);
	const setIsApprovalRequiredToStartExecution = useStore((state: Store) => state.setIsApprovalRequiredToStartExecution);
	const setIsBatchExecutionRequired = useStore((state: Store) => state.setIsBatchExecutionRequired);
	const setIsReRunAllowed = useStore((state: Store) => state.setIsReRunAllowed);
	const setUsableAsSubWorkflow = useStore((state: Store) => state.setUsableAsSubWorkflow);
	const workflowApprovalMetadata = useStore((state: Store) => state.approversMetadata);
	const canWorkflowAutoTrigger = useStore((state: Store) => state.canWorkflowAutotriggered);
	const setCanWorkflowAutoTrigger = useStore((state: Store) => state.setCanWorkflowAutotriggered);
	const setApproversMetadata = useStore((state: Store) => state.setApproversMetadata);
	const setWorkflowIntention = useStore(
		(state: Store) => state.setWorkflowIntention
	);
	const updateWFDefinition = useStore(
		(state: Store) => state.updateWFDefinition
	);
	const updateWFDefinitionAttributes = useStore(
		(state: Store) => state.updateWFDefinitionAttributes
	);
	const history = useHistory();
	const updateDescription = useStore(
		(state: Store) => state.setWorkflowDescription
	);
	const resetStore = useStore((state: Store) => state.resetStore);
	const setSelectedOptions = useStore(
		(state: Store) => state.setSelectedDomainOptions
	);
	const setWorkflowId = useStore((state: Store) => state.setWorkflowId);
	const setIsEditing = useStore((state: Store) => state.setIsEditing);
	const flashBarItemsFromStore = useStore(
		(state: Store) => state.flashBarItems
	);
	const timeOutRef = useRef<ReturnType<typeof setTimeout>>();

	const setIsReadOnly = useStore((state: Store) => state.setIsReadOnly);

	const authContext = useAuthContext();

	const { hasEditPermission, isCheckingEditPermission } = useWorkflowEditPermission(workflowId, authContext?.userName);
	const { hasCreatePermission, isCheckingCreatePermission } = useWorkflowCreatePermission(authContext?.userName);

	const isEditMode = !!workflowId;
	const isPermissionDenied = isEditMode ? !hasEditPermission : !hasCreatePermission;
	const isCheckingPagePermission = isEditMode ? isCheckingEditPermission : isCheckingCreatePermission;

	useEffect(() => {
		if (!isCheckingPagePermission && isPermissionDenied) {
			setIsReadOnly(true);
		} else {
			setIsReadOnly(false);
		}
	}, [isCheckingPagePermission, isPermissionDenied, isEditMode]);

	let getWorkflowDetailsLoading;
	let getWorkflowDataResponse;
	let getWorkflowDetailsError;

	useEffect(
		() => () => {
			resetStore();
			if (timeOutRef) {
				clearTimeout(timeOutRef.current);
			}
		},
		[]
	);

	if (workflowId) {
		({
			isLoading: getWorkflowDetailsLoading,
			error: getWorkflowDetailsError,
			data: getWorkflowDataResponse,
		} = useFetchForSDS<GetWorkflowDefinitionResponse>({
			path: `/workflows/${workflowId}`,
			method: 'GET',
			url: SNOW_NXT_WORKFLOW_ENDPOINT,
		}));
	}

	useEffect(() => {
		const workflowDefinition: WorkflowDefinition =
			getWorkflowDataResponse?.workflowDefinition || {};

		updateWorkflowName(workflowDefinition?.workflowDefinitionName || '');
		updateDescription(workflowDefinition?.description || '');
		setEntityType(workflowDefinition?.entityType || '');
		setIsApprovalRequiredToStartExecution(workflowDefinition?.approvalRequiredToStartExecution || false);
		setIsBatchExecutionRequired(workflowDefinition?.batchExecutionRequired || false);
		setIsReRunAllowed(workflowDefinition?.isReRunAllowed || false);
		setUsableAsSubWorkflow(workflowDefinition?.usableAsSubWorkflow || false);
		setCanWorkflowAutoTrigger(workflowDefinition.workflowApprovalMetadata?.isWorkflowAutoTriggeredAfterApproval || true);
		setWorkflowIntention(
			(workflowDefinition?.workflowIntention as WorkflowIntention) ||
				WorkflowIntention.CREATE
		);
		setApproversMetadata(
			workflowDefinition?.workflowApprovalMetadata?.workflowExecutionApprovers?.map(
				(approval) => ({
					approverAlias: approval?.alias || 'alias',
					approverType: {
						value: approval?.type,
					},
					numberOfApprovers: approval?.numberOfApprovers?.toString() || '0',
					level: approval?.level?.toString() || '0',
				})
			) ?? []
		);
		setSelectedOptions(
			workflowDefinition && workflowDefinition.domains
				? getSelectedOptions(DOMAIN_OPTIONS, workflowDefinition.domains) || []
				: []
		);

		if (workflowId && getWorkflowDetailsError) {
			setIsCreateWorkflowError(true);
			setFlashBarItems([
				{
					header: `Failed to Get the Workflow details for ${workflowId}`,
					type: 'error',
					loading: false,
					content: `Failed due to ${getWorkflowDetailsError}`,
				},
			]);
		}
	}, [getWorkflowDataResponse, getWorkflowDetailsError]);

	const [flashBarItems, setFlashBarItems] = useState<
		FlashbarProps.MessageDefinition[]
	>([]);

	const { onClickCB: createWorkflowCB } =
		useFetchOnUserActionUsingSDS<CreateWorkflowDefinitionResponse>();

	const getPostBody = () => {
		 return {
			workflowDefinitionName: workflowName,
			description,
			entityType,
			approvalRequiredToStartExecution,
			batchExecutionRequired,
			isReRunAllowed,
			usableAsSubWorkflow,
			workflowIntention: workflowIntention || WorkflowIntention.CREATE,
			...(approvalRequiredToStartExecution && {
				workflowApprovalMetadata: {
					isWorkflowAutoTriggeredAfterApproval: canWorkflowAutoTrigger,
					workflowExecutionApprovers: workflowApprovalMetadata?.map(
						(approval) => ({
							type: approval.approverType.value || '',
							alias: approval.approverAlias,
							numberOfApprovers: Number(approval.numberOfApprovers),
							level: Number(approval.level),
						})
					),
				},
			}),
			domains: selectedOptions.map((option) => option.label),
			createdBy: authContext.userName,
			requestId: uuidv4(),
		};
	};

	const wizardSteps = getWorkflowSteps(isEditMode).map(
		({ title, StepContent }) => ({
			title,
			content: <StepContent />,
		})
	);

	const submitCreateWorkflowMetaData = async () => {
		setIsSubmitLoadingForCreateWF(true);
		const createWFAPIResponse = await createWorkflowCB<{ workflowId: string }>({
			path: '/workflows',
			body: getPostBody(),
			method: 'POST',
			url: SNOW_NXT_WORKFLOW_ENDPOINT,
		});

		setIsModalVisibleForCreateWF(false);
		if (!createWFAPIResponse.error) {
			invalidateWorkflowCache();
			invalidatePermissionCache();
			setWorkflowId(createWFAPIResponse.workflowId);
			setIsEditing(Boolean(workflowId));
			setActiveStepIndexForCreateWF(activeStepIndexForCreateWF + 1);
			setFlashBarItems([
				{
					type: 'success',
					content: 'Successfully saved the workflow',
					dismissible: true,
					onDismiss: () => setFlashBarItems([]),
				},
			]);
			setIsSubmitLoadingForCreateWF(false);
		} else {
			setIsCreateWorkflowError(true);
			setIsSubmitLoadingForCreateWF(false);
			setFlashBarItems([
				{
					header: 'Failed to Save Workflow!',
					type: 'error',
					content: `Failed due to ${createWFAPIResponse.error}`,
					onDismiss: () => setFlashBarItems([]),
				},
			]);
		}
	};

	const submitPublishWorkflow = async () => {
		const newWFDefinition = {
			taskDefinitions,
			dependencies,
			workflowDefinition: {
				...workflowCreationMetaData,
				status: WorkflowDefinitionStatus.ACTIVE,
				workflowIntention: workflowIntention || WorkflowIntention.CREATE,
			},
		};

		updateWFDefinition(newWFDefinition, workflowIdFromGetAPI, authContext.userName || '').then(
			(message: ApiStatus) => {
				if (message === ApiStatus.SUCCESS) {
					invalidateWorkflowCache();
					const successPageURL = '/manageWorkflows';

					setRedirectUrl(`#${successPageURL}`);
					timeOutRef.current = setTimeout(() => {
						history.push(successPageURL);
					}, 10000);
				}
			}
		);
	};

	const handleConfirmation = () => {
		if (activeStepIndexForCreateWF === STEP_METADATA) {
			if (getWorkflowDataResponse || workflowIdFromGetAPI) {
				const currentWorkflowId =
					workflowIdFromGetAPI ||
					getWorkflowDataResponse.workflowDefinition.workflowId;
				const hasTasks = getWorkflowDataResponse?.taskDefinitions?.length > 0;

				if (hasTasks) {
					// PUT when tasks exist: full update propagates entityType changes to the SNOWTask table
					const newWFDefinition = {
						taskDefinitions,
						dependencies,
						workflowDefinition: workflowCreationMetaData,
					};

					if (getWorkflowDataResponse) {
						newWFDefinition.taskDefinitions.length === 0 &&
							(newWFDefinition.taskDefinitions =
								getWorkflowDataResponse.taskDefinitions),
						newWFDefinition.dependencies.length === 0 &&
								(newWFDefinition.dependencies =
									getWorkflowDataResponse.dependencies),
						(newWFDefinition.workflowDefinition =
								getWorkflowDataResponse.workflowDefinition);
					}

					updateWFDefinition(
						{
							...newWFDefinition,
							workflowDefinition: {
								...newWFDefinition.workflowDefinition,
								workflowDefinitionName: workflowName,
								description,
								entityType,
								approvalRequiredToStartExecution,
								batchExecutionRequired,
								isReRunAllowed,
								usableAsSubWorkflow,
								workflowIntention: workflowIntention || WorkflowIntention.CREATE,
								supportedTasks: [''],
								domains: selectedOptions.map((option) => option.label as string),
								version:
									version || getWorkflowDataResponse.workflowDefinition.version,
								...(approvalRequiredToStartExecution && {
									workflowApprovalMetadata: {
										isWorkflowAutoTriggeredAfterApproval: canWorkflowAutoTrigger,
										workflowExecutionApprovers: workflowApprovalMetadata.map(
											(approval) => ({
												type: approval.approverType.value || '',
												alias: approval.approverAlias,
												numberOfApprovers: Number(approval.numberOfApprovers),
												level: Number(approval.level),
											})
										),
									},
								}),
							},
						},
						currentWorkflowId,
						authContext.userName || ''
					).then((message: ApiStatus) => {
						if (message === ApiStatus.SUCCESS) {
							invalidateWorkflowCache();
							setActiveStepIndexForCreateWF(STEP_DEFINITION);
						}
					});
				} else {
					// PATCH when no tasks: only update metadata attributes, avoiding
					// backend validation that rejects empty tasks/dependencies
					updateWFDefinitionAttributes(
						{
							workflowId: currentWorkflowId,
							workflowDefinitionName: workflowName,
							description,
							entityType,
							domains: selectedOptions.map((option) => option.label as string),
							version:
								version || getWorkflowDataResponse.workflowDefinition.version,
							approvalRequiredToStartExecution,
							batchExecutionRequired,
							isReRunAllowed,
							usableAsSubWorkflow,
							workflowIntention: workflowIntention || WorkflowIntention.CREATE,
							lastUpdatedBy: authContext.userName || '',
							...(approvalRequiredToStartExecution && {
								workflowApprovalMetadata: {
									isWorkflowAutoTriggeredAfterApproval: canWorkflowAutoTrigger,
									workflowExecutionApprovers: workflowApprovalMetadata.map(
										(approval) => ({
											type: approval.approverType.value || '',
											alias: approval.approverAlias,
											numberOfApprovers: Number(approval.numberOfApprovers),
											level: Number(approval.level),
										})
									),
								},
							}),
						},
						currentWorkflowId
					).then((message: ApiStatus) => {
						if (message === ApiStatus.SUCCESS) {
							invalidateWorkflowCache();
							setActiveStepIndexForCreateWF(STEP_DEFINITION);
						}
					});
				}
			} else {
				submitCreateWorkflowMetaData();
			}
		} else if (activeStepIndexForCreateWF === STEP_DEFINITION) {
			updateWFDefinition(
				{
					dependencies,
					taskDefinitions,
					workflowDefinition: {
						...workflowCreationMetaData,
						status: WorkflowDefinitionStatus.DRAFT,
						workflowIntention: workflowIntention || WorkflowIntention.CREATE,
					},
				},
				workflowCreationMetaData.workflowId,
				authContext.userName || ''
			).then((message: ApiStatus) => {
				if (message === ApiStatus.SUCCESS) {
					invalidateWorkflowCache();
					setActiveStepIndexForCreateWF(STEP_AUTO_POPULATION);
				}
			});
		} else if (activeStepIndexForCreateWF === STEP_AUTO_POPULATION) {
			setActiveStepIndexForCreateWF(STEP_REVIEW);
		} else if (activeStepIndexForCreateWF === STEP_REVIEW) {
			submitPublishWorkflow();
		}
	};

	const handleModalCancel = () => {
		setIsModalVisibleForCreateWF(false);
	};

	const handleExitModalCancel = () => {
		setIsExitModalVisibleForCreateWF(false);
	};

	const handleExitConfirmation = () => {
		history.push('/manageWorkflows');
		setIsExitModalVisibleForCreateWF(false);
	};

	const handleNavigate = async ({ reason, requestedStepIndex }) => {
		if (reason === 'previous' || reason === 'step') {
			if (activeStepIndexForCreateWF === 1 && requestedStepIndex === 0 && workflowIdFromGetAPI) {
				fetchWFDefinition(workflowIdFromGetAPI);
			}

			setActiveStepIndexForCreateWF(requestedStepIndex);
		}

		if (reason === 'next') {
			if (activeStepIndexForCreateWF === STEP_METADATA) {
				setIstheSubmitButtonClickedForCreateWF(true);
				if (
					workflowName &&
					description &&
					entityType &&
					selectedOptions.length
				) {
					setIsModalVisibleForCreateWF(true);
				}
			}

			if (activeStepIndexForCreateWF === STEP_DEFINITION) {
				submitWorkflowDefinitionCanvasStep();
			}

			if (activeStepIndexForCreateWF === STEP_AUTO_POPULATION) {
				const saver = getConfigureAttributeSaver();
				const validationErrors = saver?.validate() ?? [];

				if (validationErrors.length > 0) {
					setFlashBarItems(
						validationErrors.map((err) => ({
							type: 'error',
							content: err,
							dismissible: true,
							onDismiss: () => setFlashBarItems([]),
						}))
					);

					return;
				}

				const advance = async () => {
					try {
						if (saver) await saver.save();
					} catch (err) {
						setFlashBarItems([
							{
								type: 'error',
								content: (err as Error)?.message ?? String(err),
								dismissible: true,
								onDismiss: () => setFlashBarItems([]),
							},
						]);

						return;
					}

					const message = await updateWFDefinition(
						{
							dependencies,
							taskDefinitions,
							workflowDefinition: {
								...workflowCreationMetaData,
								status: WorkflowDefinitionStatus.DRAFT,
								workflowIntention: workflowIntention || WorkflowIntention.CREATE,
							},
						},
						workflowCreationMetaData.workflowId,
						authContext.userName || ''
					);

					if (message === ApiStatus.SUCCESS) {
						setActiveStepIndexForCreateWF(STEP_REVIEW);
					}
				};

				advance();
			}
		}

		if (isCreateWorkflowError && Boolean(workflowId)) {
			setIsSubmitLoadingForCreateWF(true);

			return;
		}

		setFlashBarItems([]);
	};

	const handleCancel = () => {
		setIsExitModalVisibleForCreateWF(true);
	};

	return (
		<div id="parentContainerSelfServe">
			{isCheckingPagePermission && <LoadingOverlay hideOverlay={false} />}
			{!isCheckingPagePermission && (
			<>
			<Flashbar items={[
				...(isPermissionDenied
					? [{
							type: 'warning' as const,
							content: isEditMode
								? 'You do not have edit permissions for this workflow. All fields and actions are disabled.'
								: 'You do not have permission to create workflows. All fields and actions are disabled.',
							dismissible: false,
						}]
					: []),
				...flashBarItems,
				...flashBarItemsFromStore,
			]} />
			<LoadingOverlay
				hideOverlay={!(activeStepIndexForCreateWF === STEP_REVIEW && redirectUrl)}
			>
				{redirectUrl && (
					<Alert type="success" statusIconAriaLabel="Success">
						Workflow is created successfully. Redirecting to the manage
						Workflows page. Please click <a href={redirectUrl}> here </a> if you
						are not redirected with in 10 seconds
					</Alert>
				)}
			</LoadingOverlay>
			<Modal
				visible={isModalVisibleForCreateWF}
				header="Confirmation"
				closeAriaLabel="Close modal"
				data-testid="modal_test"
				onDismiss={() => handleModalCancel()}
				footer={
					<Box float="right">
						<SpaceBetween direction="horizontal" size="xs">
							<Button variant="link" onClick={() => handleModalCancel()}>
								Cancel
							</Button>
							<Button
								data-testid="confirmationButton"
								variant="primary"
								onClick={() => handleConfirmation()}
								loading={isSubmitLoadingForCreateWF || isWFDefinitionUpdating}
							>
								Confirm
							</Button>
						</SpaceBetween>
					</Box>
				}
			>
				<p>
					By clicking on Submit you agree to{' '}
					{activeStepIndexForCreateWF === STEP_REVIEW ? 'create' : 'save'} the workflow{' '}
					<b>{workflowName}</b>
				</p>
			</Modal>
			<Modal
				visible={isExitModalVisibleForCreateWF}
				header="Confirmation"
				closeAriaLabel="Close modal"
				onDismiss={() => handleExitModalCancel()}
				footer={
					<Box float="right">
						<SpaceBetween direction="horizontal" size="xs">
							<Button variant="link" onClick={() => handleExitModalCancel()}>
								Cancel
							</Button>
							<Button
								variant="primary"
								onClick={() => handleExitConfirmation()}
							>
								Confirm
							</Button>
						</SpaceBetween>
					</Box>
				}
			>
				<p>
					Are you sure you want to cancel? The changes to your workflow
					information will be discarded.
				</p>
			</Modal>
			<LoadingOverlay hideOverlay={!getWorkflowDetailsLoading} />
			<div
				className={`create-self-serve-workflow-container ${
					activeStepIndexForCreateWF === STEP_DEFINITION || activeStepIndexForCreateWF === STEP_AUTO_POPULATION ? 'hide-wizard-nav' : ''
				} ${isPermissionDenied ? 'read-only-workflow' : ''}`}
			>
				<Wizard
					i18nStrings={{
						stepNumberLabel: (stepNumber) => `Step ${stepNumber}`,
						collapsedStepsLabel: (stepNumber, stepsCount) =>
							`Step ${stepNumber} of ${stepsCount}`,
						navigationAriaLabel: 'Steps',
						previousButton: 'Previous',
						cancelButton: 'Cancel',
						nextButton:
							activeStepIndexForCreateWF === 0 && !workflowId
								? 'Save and Continue'
								: 'Next',
						submitButton: 'Publish Workflow',
						optional: 'optional',
					}}
					isLoadingNextStep={isSubmitLoadingForCreateWF}
					onNavigate={({ detail }) => handleNavigate(detail)}
					onCancel={handleCancel}
					activeStepIndex={activeStepIndexForCreateWF}
					steps={wizardSteps}
					onSubmit={() => {
						setIsModalVisibleForCreateWF(true);
					}}
				/>
			</div>
			</>)}
		</div>
	);
}
